import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faPlane,
  faWindowRestore,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import FlightDetailsModal from "./FlightDetailsModal";
import { format, parseISO, formatDuration } from "date-fns";
import { calculateFinalPrice, formatPrice } from "../../utils/pricing";

function FlightCard({ flight }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleNextStep = () => {
    try {
      const bookingData = {
        selectedOffer: { data: flight },
      };

      const flightData = encodeURIComponent(JSON.stringify(bookingData));
      console.log("Navigating to booking details with data:", bookingData);
      navigate(`/flights/details?flight=${flightData}`);
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  const finalPrice = calculateFinalPrice(flight.total_amount);

  return (
    <>
      <div className="bg-white rounded-lg p-4 md:p-6 mb-4 shadow-none border-gray-200 border">
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0">
          {/* Airline Info */}
          <div className="flex items-center justify-between md:justify-start md:w-auto">
            <div className="flex flex-col items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-lg p-1.5 flex items-center justify-center border border-gray-100">
                  {flight.slices?.[0]?.segments?.[0]?.marketing_carrier
                    ?.logo_symbol_url ? (
                    <img
                      src={
                        flight.slices[0].segments[0].marketing_carrier
                          .logo_symbol_url
                      }
                      alt={flight.slices[0].segments[0].marketing_carrier.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-gray-500">
                        {flight?.data?.slices?.[0]?.segments?.[0]
                          ?.marketing_carrier?.iata_code || "N/A"}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-gray-700 text-sm md:text-base">
                    {flight.owner.name || `Airline ${flight.operatorCode}`}
                  </p>
                </div>
              </div>

              {/* Display Cabin Class Marketing Name directly in the render */}
              <div className="mt-2">
                <p className="text-gray-700 border p-1.5 rounded-lg text-xs">
                  {flight.slices[0]?.fare_brand_name || "N/A"}
                </p>
              </div>
            </div>
            {/* Price shown on mobile only */}
            <div className="md:hidden">
              <p className={`text-xl font-bold ${config.primaryColorText}`}>
                £{flight.total_amount}
              </p>
            </div>
          </div>

          {/* Flight Details */}
          <div className="flex-grow md:px-8">
            {/* Outbound Flight */}
            <div className="flex flex-col space-y-4">
              <div className="flex-1 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-xl font-semibold">
                      {format(
                        parseISO(flight.slices[0].segments[0].departing_at),
                        "HH:mm"
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      {flight.slices[0].origin.iata_code}
                    </p>
                  </div>

                  <div className="flex-1 px-4">
                    <div className="flex flex-col items-center">
                      <span className="text-sm text-gray-500">
                        {formatDuration(flight.slices[0].duration)}
                      </span>
                      <div className="w-full flex items-center gap-1">
                        <div className="h-[2px] flex-1 bg-gray-300"></div>
                        <FontAwesomeIcon
                          icon={faPlane}
                          className="text-gray-400"
                        />
                        <div className="h-[2px] flex-1 bg-gray-300"></div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {flight.slices[0].segments.length > 1
                          ? `${flight.slices[0].segments.length - 1} stop${
                              flight.slices[0].segments.length - 1 > 1
                                ? "s"
                                : ""
                            }`
                          : "Direct"}
                      </span>
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-xl font-semibold">
                      {format(
                        parseISO(
                          flight.slices[0].segments[
                            flight.slices[0].segments.length - 1
                          ].arriving_at
                        ),
                        "HH:mm"
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      {flight.slices[0].destination.iata_code}
                    </p>
                  </div>
                </div>

                {/* Return leg */}
                {flight.slices[1] && (
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <p className="text-xl font-semibold">
                        {format(
                          parseISO(flight.slices[1].segments[0].departing_at),
                          "HH:mm"
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {flight.slices[1].origin.iata_code}
                      </p>
                    </div>

                    <div className="flex-1 px-4">
                      <div className="flex flex-col items-center">
                        <span className="text-sm text-gray-500">
                          {formatDuration(flight.slices[1].duration)}
                        </span>
                        <div className="w-full flex items-center gap-1">
                          <div className="h-[2px] flex-1 bg-gray-300"></div>
                          <FontAwesomeIcon
                            icon={faPlane}
                            className="text-gray-400 transform rotate-180"
                          />
                          <div className="h-[2px] flex-1 bg-gray-300"></div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {flight.slices[1].segments.length > 1
                            ? `${flight.slices[1].segments.length - 1} stop${
                                flight.slices[1].segments.length - 1 > 1
                                  ? "s"
                                  : ""
                              }`
                            : "Direct"}
                        </span>
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-xl font-semibold">
                        {format(
                          parseISO(
                            flight.slices[1].segments[
                              flight.slices[1].segments.length - 1
                            ].arriving_at
                          ),
                          "HH:mm"
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {flight.slices[1].destination.iata_code}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Price and Buttons - Hidden on mobile, shown on desktop */}
          <div className="hidden md:flex md:flex-col md:items-end space-y-2">
            <p className={`text-2xl font-bold ${config.primaryColorText}`}>
              {formatPrice(finalPrice, flight.total_currency)}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-full hover:bg-gray-300 transition-colors flex items-center"
            >
              Details{" "}
              <FontAwesomeIcon
                icon={faWindowRestore}
                size="sm"
                className="ml-2"
              />
            </button>
            <button
              onClick={handleNextStep}
              className={`${config.primaryColorBg} text-white rounded-full flex items-center justify-center px-6 py-2 hover:bg-purple-700 transition-colors`}
            >
              Next step
              <FontAwesomeIcon icon={faArrowRight} size="sm" className="ml-2" />
            </button>
          </div>

          {/* Mobile Buttons */}
          <div className="flex md:hidden justify-between items-center space-x-2 pt-4 border-t border-gray-100">
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-300 transition-colors flex items-center justify-center text-sm"
            >
              Details{" "}
              <FontAwesomeIcon
                icon={faChevronDown}
                size="sm"
                className="ml-2"
              />
            </button>
            <button
              onClick={handleNextStep}
              className={`flex-1 ${config.primaryColorBg} text-white rounded-full flex items-center justify-center px-4 py-2 hover:bg-purple-700 transition-colors text-sm`}
            >
              Next step
              <FontAwesomeIcon icon={faArrowRight} size="sm" className="ml-2" />
            </button>
          </div>
        </div>
      </div>

      <FlightDetailsModal
        selectedOffer={{ data: flight }}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}

export default FlightCard;
